/**
 * StructuredValue – readable view of a tool's parameters or output in the live debug panel.
 *
 * Three views: a collapsible tree, YAML, or indented JSON. Strings that hold JSON (tool outputs
 * often do) are unpacked first, so nested payloads read as structure instead of escaped text.
 */

import React, { useState } from "react";
import Box from "@mui/material/Box";

export type StructuredView = "tree" | "yaml" | "json";

export const STRUCTURED_VIEWS: StructuredView[] = ["tree", "yaml", "json"];

const VIEW_STORAGE_KEY = "theo.liveDebug.structuredView";

export function readStoredView(): StructuredView {
  try {
    const stored = window.localStorage.getItem(VIEW_STORAGE_KEY);
    if (stored && (STRUCTURED_VIEWS as string[]).includes(stored)) return stored as StructuredView;
  } catch {
    // Storage blocked: fall back to the default.
  }
  return "tree";
}

export function storeView(view: StructuredView): void {
  try {
    window.localStorage.setItem(VIEW_STORAGE_KEY, view);
  } catch {
    // Storage blocked: the choice lasts until reload.
  }
}

/** Parse strings that hold a JSON object or array, at any depth. Other values pass through. */
export function unpackJsonStrings(value: unknown, depth = 0): unknown {
  if (depth > 8) return value;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if ((trimmed.startsWith("{") && trimmed.endsWith("}")) || (trimmed.startsWith("[") && trimmed.endsWith("]"))) {
      try {
        return unpackJsonStrings(JSON.parse(trimmed), depth + 1);
      } catch {
        return value;
      }
    }
    return value;
  }
  if (Array.isArray(value)) return value.map((item) => unpackJsonStrings(item, depth + 1));
  if (value && typeof value === "object") {
    return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, unpackJsonStrings(v, depth + 1)]));
  }
  return value;
}

const YAML_PLAIN = /^[A-Za-z_./@][\w ./@()+-]*$/;
const YAML_RESERVED = /^(true|false|yes|no|on|off|null|~|y|n)$/i;

function yamlScalar(value: unknown): string {
  if (value === null || value === undefined) return "null";
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  const text = String(value);
  if (text !== "" && YAML_PLAIN.test(text) && !YAML_RESERVED.test(text) && !text.endsWith(" ") && !text.includes(": ")) {
    return text;
  }
  return JSON.stringify(text);
}

function yamlKey(key: string): string {
  return YAML_PLAIN.test(key) && !YAML_RESERVED.test(key) ? key : JSON.stringify(key);
}

function isContainer(value: unknown): value is Record<string, unknown> | unknown[] {
  return value !== null && typeof value === "object";
}

function isEmptyContainer(value: Record<string, unknown> | unknown[]): boolean {
  return Array.isArray(value) ? value.length === 0 : Object.keys(value).length === 0;
}

/** Lines for a value placed after `prefix` ("key:" or "-"), indented by `indent`. */
function yamlLines(prefix: string, value: unknown, indent: string): string[] {
  const head = prefix ? `${indent}${prefix}` : indent;
  const childIndent = prefix ? `${indent}  ` : indent;
  if (isContainer(value)) {
    if (isEmptyContainer(value)) return [`${head}${prefix ? " " : ""}${Array.isArray(value) ? "[]" : "{}"}`];
    const children = Array.isArray(value)
      ? value.flatMap((item) => yamlLines("-", item, childIndent))
      : Object.entries(value).flatMap(([k, v]) => yamlLines(`${yamlKey(k)}:`, v, childIndent));
    if (!prefix) return children;
    // A list item's first key sits on the dash line: "- id: 1".
    if (prefix === "-" && !Array.isArray(value)) return [`${head} ${children[0].trimStart()}`, ...children.slice(1)];
    return [head, ...children];
  }
  if (typeof value === "string" && value.includes("\n")) {
    const body = value.replace(/\n+$/, "").split("\n").map((line) => (line ? `${childIndent}${line}` : ""));
    return [`${head}${prefix ? " " : ""}|${value.endsWith("\n") ? "" : "-"}`, ...body];
  }
  return [`${head}${prefix ? " " : ""}${yamlScalar(value)}`];
}

export function toYaml(value: unknown): string {
  return yamlLines("", value, "").join("\n");
}

const COLORS = {
  key: "#79c0ff",
  string: "#a5d6ff",
  number: "#ffa657",
  literal: "#ff7b72",
  muted: "#8b949e",
  text: "#e6edf3",
};

function summarize(value: Record<string, unknown> | unknown[]): string {
  if (Array.isArray(value)) return `[${value.length} item${value.length === 1 ? "" : "s"}]`;
  const keys = Object.keys(value);
  const preview = keys.slice(0, 3).join(", ");
  return `{ ${preview}${keys.length > 3 ? `, … +${keys.length - 3}` : ""} }`;
}

const Leaf: React.FC<{ value: unknown }> = ({ value }) => {
  if (value === null || value === undefined) return <Box component="span" sx={{ color: COLORS.literal }}>null</Box>;
  if (typeof value === "boolean") return <Box component="span" sx={{ color: COLORS.literal }}>{String(value)}</Box>;
  if (typeof value === "number") return <Box component="span" sx={{ color: COLORS.number }}>{value}</Box>;
  const text = String(value);
  if (text.includes("\n")) {
    return (
      <Box component="span" sx={{ display: "block", color: COLORS.string, whiteSpace: "pre-wrap", pl: 1.5, borderLeft: `1px solid ${COLORS.muted}44` }}>
        {text}
      </Box>
    );
  }
  return <Box component="span" sx={{ color: COLORS.string }}>{text === "" ? '""' : text}</Box>;
};

const TreeNode: React.FC<{ label: string; value: unknown; depth: number; openDepth: number }> = ({
  label,
  value,
  depth,
  openDepth,
}) => {
  const container = isContainer(value) && !isEmptyContainer(value);
  const [open, setOpen] = useState(depth < openDepth);
  const keyLabel = (
    <Box component="span" sx={{ color: COLORS.key }}>
      {label}
      <Box component="span" sx={{ color: COLORS.muted }}>: </Box>
    </Box>
  );
  if (!container) {
    return (
      <Box sx={{ pl: 1.75 }}>
        {keyLabel}
        {isContainer(value) ? <Box component="span" sx={{ color: COLORS.muted }}>{Array.isArray(value) ? "[]" : "{}"}</Box> : <Leaf value={value} />}
      </Box>
    );
  }
  const entries: [string, unknown][] = Array.isArray(value)
    ? value.map((item, index) => [String(index), item])
    : Object.entries(value);
  return (
    <Box>
      <Box
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onClick={(event) => {
          event.stopPropagation();
          setOpen((prev) => !prev);
        }}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            setOpen((prev) => !prev);
          }
        }}
        sx={{ cursor: "pointer", userSelect: "none", "&:hover": { background: "#ffffff0d" } }}
      >
        <Box component="span" sx={{ display: "inline-block", width: 14, color: COLORS.muted }}>
          {open ? "▾" : "▸"}
        </Box>
        {keyLabel}
        {!open && <Box component="span" sx={{ color: COLORS.muted }}>{summarize(value)}</Box>}
      </Box>
      {open && (
        <Box sx={{ pl: 1.5, ml: "6px", borderLeft: `1px solid ${COLORS.muted}33` }}>
          {entries.map(([k, v]) => (
            <TreeNode key={k} label={Array.isArray(value) ? `[${k}]` : k} value={v} depth={depth + 1} openDepth={openDepth} />
          ))}
        </Box>
      )}
    </Box>
  );
};

const linkSx = {
  cursor: "pointer",
  color: COLORS.muted,
  fontSize: 10.5,
  "&:hover": { color: COLORS.text },
};

export const StructuredValue: React.FC<{ value: unknown; view: StructuredView; preSx: object }> = ({ value, view, preSx }) => {
  // Remounting the tree with a new open depth is how expand / collapse all work.
  const [tree, setTree] = useState({ openDepth: 2, version: 0 });
  const unpacked = unpackJsonStrings(value);
  if (view === "yaml") return <Box component="pre" sx={preSx}>{toYaml(unpacked)}</Box>;
  if (view === "json") return <Box component="pre" sx={preSx}>{JSON.stringify(unpacked, null, 2)}</Box>;
  if (!isContainer(unpacked)) return <Box component="pre" sx={preSx}><Leaf value={unpacked} /></Box>;
  return (
    <Box sx={{ ...preSx, whiteSpace: "normal" }}>
      <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", mb: 0.25 }}>
        <Box component="span" sx={linkSx} onClick={() => setTree((t) => ({ openDepth: 99, version: t.version + 1 }))}>
          expand all
        </Box>
        <Box component="span" sx={linkSx} onClick={() => setTree((t) => ({ openDepth: 0, version: t.version + 1 }))}>
          collapse all
        </Box>
      </Box>
      <Box key={tree.version} sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
        {Array.isArray(unpacked) ? (
          unpacked.map((item, index) => <TreeNode key={index} label={`[${index}]`} value={item} depth={0} openDepth={tree.openDepth} />)
        ) : isEmptyContainer(unpacked) ? (
          <Box component="span" sx={{ color: COLORS.muted }}>{"{}"}</Box>
        ) : (
          Object.entries(unpacked).map(([k, v]) => <TreeNode key={k} label={k} value={v} depth={0} openDepth={tree.openDepth} />)
        )}
      </Box>
    </Box>
  );
};

export default StructuredValue;
